import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  LinearProgress,
  Link,
  Modal,
  Stack,
  Typography
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Button from '../Button/Button';
import LinkButton from '../LinkButton/LinkButton';
import BotStatusContext from '../../context/BotStatusContext/BotStatusContext';
import ChatbotContext from '../../context/ChatbotContext/ChatbotContext';
import styles from './styles';

interface BuildBotModalProps {
  open: boolean;
  onClose: () => void;
}

const getStatusMessage = (progress: number): string => {
  if (progress < 1) return 'Starting to build your new bot version…';
  if (progress < 2) return 'Building new conversation flows into your chatbot…';
  if (progress < 4) return 'Connecting your dialog boxes…';
  if (progress < 7) return 'Creating new codebase…';
  if (progress < 10) return 'Checking for any text changes…';
  if (progress < 15) return 'Sending information to the AI models…';
  if (progress < 20) return 'Training the AI with your changes…';
  if (progress < 25) return 'Checking colours and settings for updates…';
  if (progress < 45) return 'Finalising your chatbot setup…';
  if (progress < 70) return 'Building the new AI model…';
  return 'Deploying your new chatbot…';
};

function BuildBotModal({
  open,
  onClose
}: BuildBotModalProps): React.ReactElement {
  const { currentChatbot } = useContext(ChatbotContext);
  const { isLoading, buildChatbot, progress, resetBuildState, currentScreen } =
    useContext(BotStatusContext);

  const [modalError, setModalError] = useState<string | null>(null);

  const testChatbotUrl = `${process.env.REACT_APP_BASE_URL}/public/chatbot-view/${currentChatbot?.uuid}`;

  useEffect(() => {
    if (open) {
      setModalError(null);
    }
  }, [open]);

  const handleBuildClick = async () => {
    setModalError(null);

    try {
      await buildChatbot();
    } catch (err: any) {
      setModalError(err.message || 'Build failed');
    }
  };

  const handleClose = () => {
    resetBuildState();
    setModalError(null);
    onClose();
  };

  const getScreenToShow = () => {
    if (modalError) return 'failure';
    return currentScreen;
  };

  const screenToShow = getScreenToShow();

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={styles.modal}>
        {screenToShow === 'build' && (
          <>
            <Typography variant='h2' fontWeight='bold' gutterBottom>
              Do you want to build your bot?
            </Typography>
            <Typography variant='body2' sx={styles.modalBodyText}>
              Bot building can take up to 20 minutes. You can track your
              progress from the status bar at the top of the page. Your chatbot
              will be live with the current version in the meantime.
            </Typography>
            <Stack direction='row' justifyContent='space-between'>
              <LinkButton
                href={testChatbotUrl}
                target='_blank'
                variant='outline'
                className='bg-[#f9a620]'
              >
                Test chatbot
              </LinkButton>
              <Button loading={isLoading} onClick={handleBuildClick}>
                Build chatbot now
              </Button>
            </Stack>
          </>
        )}
        {screenToShow === 'building' && (
          <>
            <Typography variant='h2' fontWeight='bold' gutterBottom>
              Building Your Chatbot
            </Typography>
            <Typography variant='body2' sx={{ mb: 2 }}>
              {getStatusMessage(progress)}
            </Typography>
            <Box sx={styles.progressBarContainer}>
              <LinearProgress
                variant='determinate'
                value={Math.min(progress, 100)}
                sx={styles.progressBar}
              />
            </Box>
            <Typography variant='body2' sx={{ mb: 4 }}>
              You can close this window at any time without losing progress.
              We&apos;ll notify you when your bot is done building.
            </Typography>
            <Stack direction='row' justifyContent='center'>
              <Button onClick={handleClose}>Close</Button>
            </Stack>
          </>
        )}
        {screenToShow === 'failure' && (
          <>
            <Typography
              variant='h2'
              fontWeight='bold'
              component='h2'
              gutterBottom
            >
              Build Failed
            </Typography>
            <Typography variant='body2' sx={styles.modalBodyText}>
              {modalError ||
                'There has been an issue building your chatbot. Please contact support at '}
              {!modalError && (
                <Link href='mailto:support@japeto.ai'>support@japeto.ai</Link>
              )}
              {!modalError && '.'}
            </Typography>
            <Stack direction='row' justifyContent='space-between'>
              <Button
                onClick={handleBuildClick}
                variant='outline'
                loading={isLoading}
              >
                Try again
              </Button>
              <Button onClick={handleClose} loading={isLoading}>
                Close
              </Button>
            </Stack>
          </>
        )}
        {screenToShow === 'success' && (
          <>
            <Typography variant='h1' sx={{ mb: 4 }} align='center'>
              <span style={styles.confettiEmoji}>🎉</span>
            </Typography>
            <Typography
              variant='h2'
              fontWeight='bold'
              component='h2'
              gutterBottom
            >
              Congrats! Your bot just built.
            </Typography>
            <Typography variant='body2' sx={{ mb: 4 }}>
              You can test it out on your live site now.
            </Typography>
            <Stack direction='row' justifyContent='space-between'>
              <LinkButton
                href={testChatbotUrl}
                target='_blank'
                variant='outline'
                icon={<OpenInNewIcon fontSize='small' />}
              >
                Go to live site
              </LinkButton>
              <Button onClick={handleClose}>Close</Button>
            </Stack>
          </>
        )}
      </Box>
    </Modal>
  );
}

export default BuildBotModal;
